import { cn } from '@/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import {
    FluidMenuBody,
    useFluidMenuItem,
    useFluidMenuSurface
} from './fluid-menu';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectPortal = SelectPrimitive.Portal;

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            'flex justify-between items-center bg-background disabled:opacity-50 px-3 py-2 border border-input rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring ring-offset-background focus-visible:ring-offset-2 w-full h-10 placeholder:text-muted-foreground text-[length:var(--fs-subtitle)] leading-[var(--lh-subtitle)] pointer-coarse:text-base [&>span]:line-clamp-1 disabled:cursor-not-allowed',
            className
        )}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ChevronDown className="ml-1 size-5!" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn(
            'flex justify-center items-center py-1 cursor-default',
            className
        )}
        {...props}
    >
        <ChevronUp className="w-4 h-4" />
    </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn(
            'flex justify-center items-center py-1 cursor-default',
            className
        )}
        {...props}
    >
        <ChevronDown className="w-4 h-4" />
    </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
    SelectPrimitive.ScrollDownButton.displayName;

const SelectViewport = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Viewport>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Viewport>
>(
    (
        {
            children,
            onMouseEnter,
            onMouseLeave,
            onFocus,
            ...props
        },
        forwardedRef
    ) => {
        const { hover, surfaceProps } = useFluidMenuSurface<HTMLDivElement>(
            forwardedRef,
            { onMouseEnter, onMouseLeave, onFocus }
        );
        return (
            <SelectPrimitive.Viewport {...props} {...surfaceProps}>
                <FluidMenuBody hover={hover}>{children}</FluidMenuBody>
            </SelectPrimitive.Viewport>
        );
    }
);
SelectViewport.displayName = SelectPrimitive.Viewport.displayName;

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
    <SelectPortal container={window.cl_shadowRootElement}>
        <SelectPrimitive.Content
            ref={ref}
            className={cn(
                'data-[side=left]:slide-in-from-right-2 data-[side=top]:slide-in-from-bottom-2 z-50 relative bg-background data-[side=bottom]:slide-in-from-top-2 data-[side=right]:slide-in-from-left-2 shadow-md border rounded-xl min-w-32 max-h-96 text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                position === 'popper' &&
                    'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                className
            )}
            position={position}
            {...props}
        >
            <SelectScrollUpButton />
            <SelectViewport
                className={cn(
                    'p-1.5',
                    position === 'popper' &&
                        'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)'
                )}
            >
                {children}
            </SelectViewport>
            <SelectScrollDownButton />
        </SelectPrimitive.Content>
    </SelectPortal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cn('py-1.5 pr-2 pl-8 font-semibold text-sm', className)}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, onFocus, ...props }, forwardedRef) => {
    const item = useFluidMenuItem<HTMLDivElement>(forwardedRef, onFocus);
    return (
        <SelectPrimitive.Item
            {...props}
            {...item}
            className={cn(
                'relative flex items-center data-disabled:opacity-50 py-2 pr-2.5 pl-8 rounded-lg outline-hidden w-full text-sm focus:text-accent-foreground cursor-default data-disabled:pointer-events-none select-none',
                className
            )}
        >
            <span className="left-2 absolute flex justify-center items-center w-3.5 h-3.5">
                <SelectPrimitive.ItemIndicator>
                    <Check className="w-4 h-4" />
                </SelectPrimitive.ItemIndicator>
            </span>

            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cn('bg-muted -mx-1 my-1 h-px', className)}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectGroup,
    SelectValue,
    SelectTrigger,
    SelectPortal,
    SelectContent,
    SelectLabel,
    SelectItem,
    SelectSeparator,
    SelectScrollUpButton,
    SelectScrollDownButton
};
